/**
 * Per-handler config Zod schemas — single source of truth for the shape of
 * `ActionBinding.handlerConfig` for each handler id in `HANDLER_REGISTRY`.
 *
 * Why: `ActionBinding.handlerConfig` is `z.record(z.string(), JsonValueSchema)`
 * at the binding level (JSON can hold anything). Every wizard consumer that
 * reads a named field (`webhookUrl`, `callbackUrl`, `objectApiName`, ...) was
 * re-asserting that shape with `as Record<string, string>`, `as WebhookConfig`,
 * etc. Those casts are trust-boundary assertions over untrusted JSON — the
 * blueprint was authored by a human via the admin UI or imported from storage.
 *
 * This file puts each handler's accepted shape in one place and exposes a
 * typed accessor `getHandlerConfig(binding, handler)` that returns the narrowed
 * shape (or `null` on parse failure — the caller decides how to recover).
 *
 * Note on registry alignment: `HANDLER_REGISTRY[handlerId].configSchema` is a
 * ConfigField[] describing field presentation metadata (label, hint, type,
 * required). This file's schemas describe the runtime *value* shape. The two
 * are complementary — registry drives form rendering, schema drives parse.
 */

import { z } from 'zod';
import type { ActionBinding } from './action-binding.schema';
import { getHandlerEntry, type HandlerType } from './registries/handlers';
import type { JsonValue } from '../schemas/json-value';

// Per-handler schemas

/** No-op handler — no config. */
export const NoneHandlerConfigSchema = z.object({}).passthrough();

/**
 * Webhook handler config.
 *
 * Fields mirror `HANDLER_REGISTRY.webhook.configSchema`:
 *  - callbackUrl is the destination URL (required at publish-time; optional
 *    at parse-time because drafts may be in progress)
 *  - referenceKeyField optionally names the field whose value becomes the
 *    `referenceKey` in the outgoing webhook payload.
 *  - webhookAuthHeader / webhookAuth are the auth header name/value pair.
 *  - webhookUrl is a legacy alias for callbackUrl used by a few ExportStep
 *    summary sites and some imported drafts; accepted so legacy bindings
 *    parse cleanly.
 *
 * `.passthrough()` keeps unknown keys around so a later publish-time schema
 * bump doesn't silently drop data.
 */
export const WebhookHandlerConfigSchema = z.object({
  callbackUrl: z.string().optional(),
  /** Legacy alias for `callbackUrl` — some drafts / summary sites use this. */
  webhookUrl: z.string().optional(),
  referenceKeyField: z.string().optional(),
  webhookAuthHeader: z.string().optional(),
  webhookAuth: z.string().optional(),
}).passthrough();

/**
 * Salesforce PATCH handler config.
 *
 * Mirrors `HANDLER_REGISTRY.salesforce_patch.configSchema`:
 *  - objectApiName — target SObject API name
 *  - recordIdField — which blueprint data field carries the record id
 */
export const SalesforcePatchHandlerConfigSchema = z.object({
  objectApiName: z.string().optional(),
  recordIdField: z.string().optional(),
}).passthrough();

/**
 * Approve-message handler config.
 *
 * Empty by design, mirroring `HANDLER_REGISTRY.approve_message.configSchema`: the
 * handler reads the draft and the recipient off the artifact it is acting on, so
 * there is nothing to configure per binding. Still `.passthrough()`, so a future
 * option can be added without invalidating bindings written today.
 */
export const ApproveMessageHandlerConfigSchema = z.object({}).passthrough();

/**
 * Internal-service handler config.
 *
 * Mirrors `HANDLER_REGISTRY.internal.configSchema`:
 *  - service — the registered internal service name (`namespace.action`).
 *    Optional at parse-time (drafts may be in progress); required at
 *    publish-time via the registry field's `required: true`, and validated
 *    against the live registration set by
 *    `services/execution-handlers/handler-validation.ts`.
 */
export const InternalHandlerConfigSchema = z.object({
  service: z.string().optional(),
}).passthrough();

// Registry map — schema per handler id

/**
 * Map from handler id -> parse schema. Keyed by registry id so
 * `terra/no-duplicate-schema-name` sees a single export name per schema.
 *
 * Use via `getHandlerConfig(binding, 'webhook')`.
 */
const HANDLER_CONFIG_SCHEMAS = {
  none: NoneHandlerConfigSchema,
  webhook: WebhookHandlerConfigSchema,
  salesforce_patch: SalesforcePatchHandlerConfigSchema,
  approve_message: ApproveMessageHandlerConfigSchema,
  internal: InternalHandlerConfigSchema,
} as const satisfies Record<HandlerType, z.ZodType>;

/** Type-level lookup: `HandlerConfigFor<'webhook'>` -> `WebhookHandlerConfig`. */
export type HandlerConfigFor<H extends HandlerType> =
  z.infer<(typeof HANDLER_CONFIG_SCHEMAS)[H]>;

// Typed accessor

/**
 * Narrow a binding's `handlerConfig` to the shape associated with `handler`.
 * Returns `null` if the config does not parse as that handler's shape — the
 * caller decides whether to treat that as "not configured yet" (fine in the
 * wizard draft) or as a validation error (publish-time).
 *
 * The binding-level `handlerConfig` type is already `Record<string, JsonValue>`
 * (defaulted to `{}`), so this is genuinely a narrowing — we're not asserting
 * the presence of any specific key, just re-shaping the already-validated JSON
 * object to the handler's known surface.
 *
 * Implementation note: the final assertion is unavoidable at this boundary —
 * `HANDLER_CONFIG_SCHEMAS[handler]` has the union of all handler output types,
 * and TypeScript cannot prove that `HANDLER_CONFIG_SCHEMAS[H]['_output']` is
 * the same as `HandlerConfigFor<H>` without per-branch dispatch. At runtime
 * the schema-per-key map guarantees it: we parse with the schema registered
 * at exactly this key, so the parsed data IS HandlerConfigFor<H>.
 */
export function getHandlerConfig<H extends HandlerType>(
  binding: { handlerConfig?: ActionBinding['handlerConfig'] },
  handler: H,
): HandlerConfigFor<H> | null {
  const schema = HANDLER_CONFIG_SCHEMAS[handler];
  const parsed = schema.safeParse(binding.handlerConfig ?? {});
  if (!parsed.success) return null;
  return parsed.data as HandlerConfigFor<H>;
}

// Destination resolution

/**
 * Resolve the destination URL configured for a handler, driven by the
 * registry's `configSchema` rather than a hardcoded key name.
 *
 * Why this exists: wizard/studio summary surfaces each re-derived "the
 * webhook URL is `callbackUrl` (or the legacy `webhookUrl` alias)" knowledge
 * independently, which drifts whenever a handler's config field is renamed
 * or a new url-bearing handler is added. This is the one place that owns
 * both facts:
 *  1. Which config key holds the destination — read off
 *     `HANDLER_REGISTRY[handlerId].configSchema` (the first field with
 *     `type: 'url'`), not a hardcoded `callbackUrl`.
 *  2. The `webhookUrl` legacy alias — resolved through
 *     `WebhookHandlerConfigSchema` (the schema that documents it), not a raw
 *     `config.webhookUrl` key read.
 *
 * Returns `undefined` if the handler has no `url`-typed config field (e.g.
 * `salesforce_patch` — out of scope, see plan 2026-07-31-002 U3) or if the
 * resolved value is empty/not a string.
 */
export function handlerDestination(
  handlerId: string,
  config: Record<string, JsonValue>,
): string | undefined {
  const entry = getHandlerEntry(handlerId);
  const urlField = entry?.configSchema.find((field) => field.type === 'url');
  const value = urlField ? config[urlField.key] : undefined;
  if (typeof value === 'string' && value) return value;

  if (handlerId === 'webhook') {
    const parsed = WebhookHandlerConfigSchema.safeParse(config);
    if (parsed.success && parsed.data.webhookUrl) return parsed.data.webhookUrl;
  }

  return undefined;
}


